import {
  HOUR,
  isEmpty,
  messageFromError,
  type APIError,
  type UserId,
  type UserLoginHistoryItem,
  type UserLoginsInput,
  type UserLoginsSuccess,
} from '@terros/common'
import { useInfiniteQuery, type InfiniteData, type QueryKey, type UseInfiniteQueryResult } from '@tanstack/react-query'
import { userLoginsInfiniteKey } from '../keys'
import { useServerRequest } from '../api'

type UserLoginsResponse = {
  history: UserLoginHistoryItem[]
  page?: number
  hasMore?: boolean
}

export type UseInfiniteUserLoginsResult = Omit<UseInfiniteQueryResult<InfiniteData<UserLoginsSuccess>>, 'error'> & {
  error?: string
  history: UserLoginHistoryItem[]
}

export function useInfiniteUserLogins(userId: UserId | undefined, enabled = true): UseInfiniteUserLoginsResult {
  const serverRequest = useServerRequest<UserLoginsInput, UserLoginsSuccess>('/user/logins')
  const query = useInfiniteQuery<
    UserLoginsSuccess,
    APIError,
    InfiniteData<UserLoginsSuccess>,
    QueryKey,
    UserLoginsInput
  >({
    queryKey: userLoginsInfiniteKey(userId),
    queryFn: async ({ pageParam }) => {
      const result = await serverRequest(pageParam)
      return result
    },
    initialPageParam: { userId },
    getNextPageParam: (lastPage) => {
      if (isEmpty(lastPage.history) || !lastPage.hasMore) return undefined
      return {
        userId,
        page: lastPage.page,
      }
    },
    enabled: enabled && userId !== undefined,
    staleTime: HOUR,
    gcTime: 6 * HOUR,
  })

  const error = messageFromError(query.error)
  const history = query.data?.pages.flatMap((page) => page.history) ?? []

  return {
    ...query,
    error,
    history,
  }
}
